// ── Plan configuration — single source of truth ──────
// Monthly prices: Basic $60, Pro $165, Enterprise $410
// Yearly discount: 5% off
// Yearly = monthly * 12 * 0.95

export const PLANS = {
  trial: {
    name: 'Free Trial',
    key: 'trial',
    price: { monthly: 0, yearly: 0 },
    color: '#94A3B8',
    colorBg: 'rgba(148,163,184,0.1)',
    colorBorder: 'rgba(148,163,184,0.25)',
    gradient: 'linear-gradient(135deg,#475569,#334155)',
    badge: '⏰',
    badgeLabel: 'TRIAL',
    popular: false,
    jobSlots: 1,
    applicationLimit: 50,
    featuredJobs: false,
    resumeAccess: false,
    analyticsEnabled: false,
    advancedFilters: false,
    teamSlots: 1,
    contactApplicants: false,
    exportApplicants: false,
    priorityPlacement: false,
    verifiedBadge: false,
    highlights: [
      '1 active job posting',
      '50 total applications',
      'Basic applicant view',
      '14-day duration',
    ],
    limits: [
      'No resume access',
      'No applicant contact',
      'No analytics',
      'No featured listings',
    ],
  },

  basic: {
    name: 'Basic',
    key: 'basic',
    price: { monthly: 60, yearly: Math.round(60 * 12 * 0.95) },
    color: '#4F8EF7',
    colorBg: 'rgba(79,142,247,0.1)',
    colorBorder: 'rgba(79,142,247,0.3)',
    gradient: 'linear-gradient(135deg,#4F8EF7,#3B82F6)',
    badge: '⭐',
    badgeLabel: 'BASIC',
    popular: false,
    jobSlots: 3,
    applicationLimit: 200,
    featuredJobs: false,
    resumeAccess: true,
    analyticsEnabled: false,
    advancedFilters: false,
    teamSlots: 1,
    contactApplicants: true,
    exportApplicants: false,
    priorityPlacement: false,
    verifiedBadge: false,
    highlights: [
      '3 active job postings',
      '200 applications/month',
      'Resume download access',
      'Contact applicants directly',
      'Shortlist & interview flow',
    ],
    limits: [
      'No featured listings',
      'No analytics dashboard',
      'No applicant export',
    ],
  },

  pro: {
    name: 'Pro',
    key: 'pro',
    price: { monthly: 165, yearly: Math.round(165 * 12 * 0.95) },
    color: '#7C3AED',
    colorBg: 'rgba(124,58,237,0.1)',
    colorBorder: 'rgba(124,58,237,0.35)',
    gradient: 'linear-gradient(135deg,#7C3AED,#6D28D9)',
    badge: '🚀',
    badgeLabel: 'PRO',
    popular: true,
    jobSlots: 10,
    applicationLimit: -1,
    featuredJobs: true,
    resumeAccess: true,
    analyticsEnabled: true,
    advancedFilters: true,
    teamSlots: 3,
    contactApplicants: true,
    exportApplicants: true,
    priorityPlacement: true,
    verifiedBadge: true,
    highlights: [
      '10 active job postings',
      'Unlimited applications',
      'Featured job listings',
      'Full analytics dashboard',
      'Advanced applicant filters',
      'Export applicants to CSV',
      'Verified company badge',
      'Priority search placement',
      '3 team members',
    ],
    limits: [],
  },

  enterprise: {
    name: 'Enterprise',
    key: 'enterprise',
    price: { monthly: 410, yearly: Math.round(410 * 12 * 0.95) },
    color: '#F59E0B',
    colorBg: 'rgba(245,158,11,0.1)',
    colorBorder: 'rgba(245,158,11,0.35)',
    gradient: 'linear-gradient(135deg,#F59E0B,#D97706)',
    badge: '👑',
    badgeLabel: 'ENTERPRISE',
    popular: false,
    jobSlots: -1,
    applicationLimit: -1,
    featuredJobs: true,
    resumeAccess: true,
    analyticsEnabled: true,
    advancedFilters: true,
    teamSlots: -1,
    contactApplicants: true,
    exportApplicants: true,
    priorityPlacement: true,
    verifiedBadge: true,
    highlights: [
      'Unlimited job postings',
      'Unlimited applications',
      'Top placement in all searches',
      'Full analytics + custom reports',
      'Unlimited team members',
      'Dedicated account manager',
      'Priority support 24/7',
      'API access (coming soon)',
      'Custom branding on listings',
    ],
    limits: [],
  },
};

export const PLAN_ORDER = ['trial', 'basic', 'pro', 'enterprise'];

export const getPlan = (planKey) =>
  PLANS[planKey] || PLANS.trial;

export const isHigherPlan = (current, target) =>
  PLAN_ORDER.indexOf(target) > PLAN_ORDER.indexOf(current);

export const canUseFeature = (planKey, feature) =>
  getPlan(planKey)[feature] === true;

export const hasJobSlots = (company) => {
  const plan = getPlan(company?.plan || 'trial');
  if (plan.jobSlots === -1) return true;
  return (company?.slots_used || 0) < plan.jobSlots;
};

export const getRemainingSlots = (company) => {
  const plan = getPlan(company?.plan || 'trial');
  if (plan.jobSlots === -1) return '∞';
  return Math.max(0, plan.jobSlots - (company?.slots_used || 0));
};

export const getApplicationsLeft = (company) => {
  const plan = getPlan(company?.plan || 'trial');
  if (plan.applicationLimit === -1) return '∞';
  return Math.max(
    0,
    plan.applicationLimit - (company?.applications_used_this_month || 0)
  );
};

export const formatPrice = (plan, cycle) => {
  const p = getPlan(plan);
  if (p.price.monthly === 0) return 'Free';
  const amount = cycle === 'yearly'
    ? (p.price.yearly / 12).toFixed(0)
    : p.price.monthly;
  return `$${amount}/mo`;
};

export const formatYearlyTotal = (plan) => {
  const p = getPlan(plan);
  if (p.price.yearly === 0) return '';
  return `$${p.price.yearly}/yr`;
};

export const getSavings = (plan) => {
  const p = getPlan(plan);
  if (p.price.monthly === 0) return 0;
  const monthlyTotal = p.price.monthly * 12;
  return monthlyTotal - p.price.yearly;
};